import { useSyncExternalStore } from "react";

/**
 * Which clock the site's times are written in (DSPRT-299): the viewer's own
 * (the default, and what every time was already in) or US Eastern, the
 * league's. Kept on the device. Days a game is FILED under are the league's
 * either way; this only moves the times printed on them.
 */
export type ZoneChoice = "local" | "et";

const KEY = "statsummit:tz";
const EASTERN = "America/New_York";

function read(): ZoneChoice {
  try {
    return localStorage.getItem(KEY) === "et" ? "et" : "local";
  } catch {
    return "local";
  }
}

let choice: ZoneChoice = read();
const listeners = new Set<() => void>();

export function zoneChoice(): ZoneChoice {
  return choice;
}

export function setZoneChoice(next: ZoneChoice): void {
  if (next === choice) return;
  choice = next;
  try { localStorage.setItem(KEY, next); } catch { /* private mode: this visit only */ }
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useZoneChoice(): ZoneChoice {
  return useSyncExternalStore(subscribe, zoneChoice, zoneChoice);
}

/* ------------------------------------------------------------------ *
 * Units and clock (DSPRT-303): the other two display choices, kept the
 * same way. Until one is chosen they start as the device has them
 * (DSPRT-324): centimetres and kilograms outside the few countries that
 * measure a person in feet, and a 24-hour clock where the device's locale
 * keeps one. A choice made here always wins over the device.
 * ------------------------------------------------------------------ */

export type Units = "imperial" | "metric";
export type Clock = "12" | "24";

const UNITS_KEY = "statsummit:units";
const CLOCK_KEY = "statsummit:clock";

/** Where a person's height is given in feet and inches (CLDR's person-height preference, and the two other imperial countries). */
const FEET_REGIONS = new Set(["US", "CA", "GB", "LR", "MM"]);

/** Feet and pounds or centimetres and kilograms, from the device's region. A language with no region ("en") takes its likeliest one. */
export function deviceUnits(locale: string = navigator.language): Units {
  try {
    const region = new Intl.Locale(locale).maximize().region;
    return !region || FEET_REGIONS.has(region) ? "imperial" : "metric";
  } catch {
    return "imperial";
  }
}

/** A 12- or 24-hour clock, as the device's locale writes the time. */
export function deviceClock(locale: string = navigator.language): Clock {
  try {
    const cycle = new Intl.DateTimeFormat(locale, { hour: "numeric" }).resolvedOptions().hourCycle;
    return cycle === "h23" || cycle === "h24" ? "24" : "12";
  } catch {
    return "12";
  }
}

/** The stored choice when there is one; otherwise what the device suggests. */
function readOne<T extends string>(key: string, values: readonly T[], device: () => T): T {
  try {
    const stored = localStorage.getItem(key) as T | null;
    if (stored && values.includes(stored)) return stored;
  } catch {
    // No storage: the device's own.
  }
  return device();
}

let units: Units = readOne<Units>(UNITS_KEY, ["imperial", "metric"], deviceUnits);
let clock: Clock = readOne<Clock>(CLOCK_KEY, ["12", "24"], deviceClock);

export const unitsChoice = (): Units => units;
export const clockChoice = (): Clock => clock;

export function setUnits(next: Units): void {
  if (next === units) return;
  units = next;
  try { localStorage.setItem(UNITS_KEY, next); } catch { /* private mode: this visit only */ }
  listeners.forEach((l) => l());
}

export function setClock(next: Clock): void {
  if (next === clock) return;
  clock = next;
  try { localStorage.setItem(CLOCK_KEY, next); } catch { /* private mode: this visit only */ }
  listeners.forEach((l) => l());
}

export const useUnits = (): Units => useSyncExternalStore(subscribe, unitsChoice, unitsChoice);
export const useClock = (): Clock => useSyncExternalStore(subscribe, clockChoice, clockChoice);

/** All three choices as one string: what the page is keyed on, so changing any of them redraws every time and measure. */
const displayKey = () => `${choice}:${units}:${clock}`;
export const useDisplayKey = (): string => useSyncExternalStore(subscribe, displayKey, displayKey);

/**
 * The options for a clock time: "1:30 AM" or "01:30", in the chosen zone.
 * Spread first, so a call can still add a weekday beside it.
 */
export function clockOpts(): Intl.DateTimeFormatOptions {
  return clock === "24"
    ? { hour: "2-digit", minute: "2-digit", hourCycle: "h23", ...zoneOpts() }
    : { hour: "numeric", minute: "2-digit", ...zoneOpts() };
}

/** A height the feed writes in feet and inches (6' 8", 6'9", 6-8): as it is, or in centimetres. */
export function showHeight(text: string | null | undefined): string | null {
  if (!text) return text ?? null;
  if (units === "imperial") return text;
  const m = /^\s*(\d)\s*['′-]\s*(\d{1,2}(?:\.\d+)?)?\s*(?:"|″|'')?\s*$/.exec(text);
  if (!m) return text;
  return `${Math.round((Number(m[1]) * 12 + Number(m[2] ?? 0)) * 2.54)} cm`;
}

/** A weight the feed writes in pounds ("224 lbs"): as it is, or in kilograms. */
export function showWeight(text: string | null | undefined): string | null {
  if (!text) return text ?? null;
  if (units === "imperial") return text;
  const m = /^\s*(\d{2,3}(?:\.\d+)?)\s*(?:lbs?\.?|pounds)?\s*$/i.exec(text);
  if (!m) return text;
  return `${Math.round(Number(m[1]) * 0.45359237)} kg`;
}

/** A length in inches (a fighter's height or reach) in centimetres, or null to leave it to the caller's own feet-and-inches. */
export function inchesAsCm(inches: number): string | null {
  return units === "metric" ? `${Math.round(inches * 2.54)} cm` : null;
}

/** Spread into a toLocale…String options bag wherever a time or its day is in the viewer's zone. */
export function zoneOpts(): { timeZone?: string } {
  return choice === "et" ? { timeZone: EASTERN } : {};
}

/**
 * "GMT+3" for a zone (undefined = the device's) on a given day, now by
 * default: a December card said GMT+3 in Jerusalem, today's summer offset
 * (DSPRT-432, B-008).
 */
function offsetOf(timeZone: string | undefined, at: Date = new Date()): string {
  try {
    const part = new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: "shortOffset" })
      .formatToParts(at).find((p) => p.type === "timeZoneName");
    return part?.value ?? "";
  } catch {
    return "";
  }
}

/** The device's zone as a place: "Asia/Jerusalem" -> "Jerusalem". */
function devicePlace(): string {
  try {
    const id = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const city = id?.split("/").pop()?.replace(/_/g, " ");
    return city && city !== "UTC" ? city : "your time";
  } catch {
    return "your time";
  }
}

const withOffset = (name: string, offset: string) => (offset ? `${name} (${offset})` : name);

/** "Jerusalem (GMT+3)" or "US Eastern (GMT-4)", with the offset on `at` (now by default). */
export function zoneLabel(c: ZoneChoice = choice, at?: Date): string {
  return c === "et" ? withOffset("US Eastern", offsetOf(EASTERN, at)) : withOffset(devicePlace(), offsetOf(undefined, at));
}

/**
 * The zone with the offset of the times listed (B-107): one offset when they
 * all share it, and each change when the clocks move within the list:
 * "Jerusalem (GMT+3, then GMT+2 from 25 Oct)"; a season-long list crosses
 * two (B-118), "Jerusalem (GMT+3, GMT+2 from 25 Oct, GMT+3 from 27 Mar)".
 * Neighbouring times are compared, not just the first and the last, which
 * share an offset when the list runs from one summer to the next.
 */
export function zoneLabelFor(times: ReadonlyArray<string | null | undefined>, c: ZoneChoice = choice): string {
  const zone = c === "et" ? EASTERN : undefined;
  const name = c === "et" ? "US Eastern" : devicePlace();
  const ts = times.map((t) => (t ? Date.parse(t) : NaN)).filter((t) => !Number.isNaN(t)).sort((a, b) => a - b);
  if (ts.length === 0) return zoneLabel(c);
  const at = (t: number) => offsetOf(zone, new Date(t));
  const first = at(ts[0]);
  const changes: Array<{ offset: string; day: string }> = [];
  // A week at a time across each gap, so two changes between far-apart times aren't missed.
  const WEEK = 7 * 86_400_000;
  for (let i = 1; i < ts.length; i++) {
    for (let from = ts[i - 1]; from < ts[i]; from += WEEK) {
      const to = Math.min(from + WEEK, ts[i]);
      const before = at(from), after = at(to);
      if (before === after) continue;
      // The hour it changed, within that week.
      let lo = from, hi = to;
      while (hi - lo > 3_600_000) {
        const mid = (lo + hi) / 2;
        if (at(mid) === before) lo = mid; else hi = mid;
      }
      changes.push({ offset: after, day: new Date(hi).toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: zone }) });
    }
  }
  if (changes.length === 0) return withOffset(name, first);
  if (changes.length === 1) return `${name} (${first}, then ${changes[0].offset} from ${changes[0].day})`;
  return `${name} (${[first, ...changes.map((x) => `${x.offset} from ${x.day}`)].join(", ")})`;
}

/** True when the device already keeps US Eastern time, so there is nothing to choose between. */
export function deviceIsEastern(): boolean {
  return offsetOf(undefined) === offsetOf(EASTERN);
}
